/**
 * This module manages the entire choreography of:
 *
 * 1) Uploading a file to S3
 * 2) Attaching it to an object in our DB
 * 3) Managing queues for uploads, conversions, etc
 * 4) Displaying progress in the UI
 *
 * For attachment to DB models and process choreography, see attachments.js
 * For files collection CRUD, see files.js
 * For uploading (to s3), see upload.js
 *
 * @file
 * @author Jason Haddix <jason@thebrandingfarm.com>
 * @author Benjamin Listwon <ben@thebrandingfarm.com>
 */

import hash from 'object-hash'
import cloneDeep from 'lodash-es/cloneDeep'
import isEqual from 'lodash-es/isEqual'

import slugify from '@/js/slugify'

import store from '@/store'

import {
	VUEX_ATTACHMENT_QUEUE_MANAGER_ADD_FILES,
	VUEX_ATTACHMENT_QUEUE_MANAGER_ASSIGN_USAGE_TYPE,
	VUEX_ATTACHMENT_QUEUE_MANAGER_ASSIGN_NOTE,
	VUEX_ATTACHMENT_QUEUE_MANAGER_ASSIGN_COMMENT_ID,
	VUEX_ATTACHMENT_QUEUE_MANAGER_UPDATE_FILE_STATUS,
	VUEX_ATTACHMENT_QUEUE_MANAGER_REMOVE_HOLDING_FILE,
	VUEX_ATTACHMENT_QUEUE_MANAGER_REMOVE_FILE,
	VUEX_ATTACHMENT_QUEUE_MANAGER_CLEAR_BUCKET,
	VUEX_ATTACHMENT_QUEUE_MANAGER_ENQUEUE_UPLOAD,
	VUEX_ATTACHMENT_QUEUE_MANAGER_UPLOAD_FILE,
	VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_CREATE_SUCCESS,
	VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_CREATE_FAILURE,
	VUEX_ATTACHMENT_QUEUE_MANAGER_ATTACH_TO_RESOLUTION_SUCCESS,
	VUEX_ATTACHMENT_QUEUE_MANAGER_PROCESS_FILE,
	VUEX_ATTACHMENT_QUEUE_MANAGER_QUEUE_JOB_ATTACHMENTS,
	VUEX_ATTACHMENT_QUEUE_MANAGER_HANDLE_PROCESSING_COMPLETE,
	VUEX_ATTACHMENT_QUEUE_MANAGER_EVENT_DISPATCHER,

	VUEX_ATTACHMENT_QUEUE_MANAGER_ATTACH,
	VUEX_ATTACHMENT_QUEUE_MANAGER_CREATE_JOB_ATTACHMENTS,

	VUEX_ATTACHMENT_QUEUE_MANAGER_UPLOAD_PROGRESS,
	VUEX_ATTACHMENT_QUEUE_MANAGER_ASSIGN_S3_KEY,
	VUEX_ATTACHMENT_QUEUE_MANAGER_HANDLE_UPLOAD_RESULT,
	VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_MOVE_TO_BUCKET,

	VUEX_ATTACHMENT_QUEUE_MANAGER_HASH_PUSH_FILE,
	VUEX_ATTACHMENT_QUEUE_MANAGER_HASH_POP_FILE } from '@/store/constants/attachment_queue_manager'

import {
	VUEX_FILE_CREATE_REQUEST,
	VUEX_FILE_UPDATE_REQUEST } from '@/store/constants/files'

import {
	VUEX_UPLOAD_S3_QUEUE_SIZE,
	VUEX_UPLOAD_REQUEST } from '@/store/constants/upload'

import {
	VUEX_ATTACHMENT_PERFORM_ATTACH_TO_RESOLUTION,
	VUEX_ATTACHMENTS_DELETE_ATTACHMENT } from '@/store/constants/attachments'

import {
	VUEX_CALENDAR_FILE_UPLOAD_SUCCESS } from '@/store/constants/calendarItems'

import {
	VUEX_UI_SHOW_GLOBAL_UPLOADER,
	VUEX_UI_HIDE_GLOBAL_UPLOADER } from '@/store/constants/ui'
import {
    VUEX_SNACKBAR_SHOW
} from '@/store/constants/snackbar'


const AQM_HOLDING_BUCKET    = 'filesHolding'
const AQM_QUEUED_BUCKET     = 'filesQueued'
const AQM_UPLOADING_BUCKET  = 'filesUploading'
const AQM_PROCESSING_BUCKET = 'filesProcessing' // Signal from BE not implemented yet, v2.1
const AQM_COMPLETED_BUCKET  = 'filesCompleted'
const AQM_FAILED_BUCKET     = 'filesFailed'


const state = {
	fileHash        : {},    // The file objects go in the "hashmap", use pushFile and popFile
								 // to auto-magically tag it with an id, and get that back for tracing
								 // throughout the process.

								 // These now, will just have IDs, as per some reading I was doing from
								 // the Vue core team for maintaining reactivity, etc. using Vue.set
								 //
	filesHolding    : [],    // Files added from upload but holding for user input | comment attachment usage
	filesQueued     : [],    // Files ready to be processed
	filesAttaching  : [],    // ONLY used for file that are being attached and NOT uploaded
	filesUploading  : [],    // Files that are being uploaded
	filesProcessing : [],    // Files currently being processed; Signal from BE not implemented yet, v2.1
	filesCompleted  : [],    // Successfully uploaded files
	filesFailed     : [],    // File that failed upload
}


const getters = {

	// Could consolidate into one getter? Made verbose so we can extend if needed later.
	hasHoldingFiles(state) { return state.filesHolding.length > 0 },
	hasQueuedFiles(state) { return state.filesQueued.length > 0 },
	hasUploadingFiles(state) { return state.filesUploading.length > 0 },
	hasProcessingFiles(state) { return state.filesProcessing.length > 0 },
	hasAttachingFiles(state) { return state.filesAttaching.length > 0 },
	hasCompletedFiles(state) { return state.filesCompleted.length > 0 },
	hasFailedFiles(state) { return state.filesFailed.length > 0 },


	// In all of these, the !params case is if you want everything from a particular
	// bucket that is not filtered. The params is an object that filters the buckets
	// by the model and model_id so youc an get uploads, say, only for a specific
	// comment.
	getHoldingFiles(state) {
		return params => state.filesHolding.map(hashId => state.fileHash[hashId]).reduce(function(filtered, fileObject) {
			if (!params || isEqual(fileObject.attach_to, params.attach_to)) {
				filtered.push(fileObject)
			}
			return filtered
		  }, [])
	},

	getQueuedFiles(state) {
		return params => state.filesQueued.map(hashId => state.fileHash[hashId]).reduce(function(filtered, fileObject) {
			if (!params || isEqual(fileObject.attach_to, params.attach_to)) {
				filtered.push(fileObject)
			}
			return filtered
		  }, [])
	},

	getUploadingFiles(state) {
		return params => state.filesUploading.map(hashId => state.fileHash[hashId]).reduce(function(filtered, fileObject) {
			if (!params || isEqual(fileObject.attach_to, params.attach_to)) {
				filtered.push(fileObject)
			}
			return filtered
		  }, [])
	},

	getProcessingFiles(state) {
		return params => state.filesProcessing.map(hashId => state.fileHash[hashId]).reduce(function(filtered, fileObject) {
			if (!params || isEqual(fileObject.attach_to, params.attach_to)) {
				filtered.push(fileObject)
			}
			return filtered
		  }, [])
	},

	getAttachingFiles(state) {
		return params => state.filesAttaching.map(hashId => state.fileHash[hashId]).reduce(function(filtered, fileObject) {
			if (!params || isEqual(fileObject.attach_to, params.attach_to)) {
				filtered.push(fileObject)
			}
			return filtered
		  }, [])
	},

	getCompletedFiles(state) {
		return params => state.filesCompleted.map(hashId => state.fileHash[hashId]).reduce(function(filtered, fileObject) {
			if (!params || isEqual(fileObject.attach_to, params.attach_to)) {
				filtered.push(fileObject)
			}
			return filtered
		  }, [])
	},
}


const actions = {

	/**
	 * SECTION 1 - Init: Get files into the manager
	 */

	// Core uploader calls this with a list of file objects
	[VUEX_ATTACHMENT_QUEUE_MANAGER_ADD_FILES]: async({ commit, dispatch }, payload) => {
	    // Push each file onto the corresponding bucket individually (made interface to
		// VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_MOVE_TO_BUCKET more homogenous)

        let fileObj
        let payloadItems

        //From Matt: this had been coded to look for payload.files
        //but I noticed that files were coming in directly to payload
        //so i have it scan for both
        if (payload.files) {
            payloadItems = payload.files
        } else {
            payloadItems = payload
        }
		for (let i=0;i<payloadItems.length;i++) {
			// Just grab the item as fileObj
			fileObj = payloadItems[i]

			// Clean up filename for S3 / CloudConvert
			if (fileObj.usage_type && fileObj.usage_type !== SILA_FILE_USAGE_TYPE_FTP_URL)
				fileObj.filename = slugify(fileObj.filename)

			// Generate a unique id for the file (added random in case there are two files with
			// same name in same session, not perfect, but better)
			let hashId

			if (fileObj.file) {
				// for all uploads, except ...
				hashId = hash( fileObj.file.name + (Math.floor(Math.random() * 100000) + 1) )
			} else {
				// For search / FTP assets
				hashId = hash( fileObj.filename + (Math.floor(Math.random() * 100000) + 1) )
			}

			// Add our hashId to the file object for tracing. Be careful to
			// not trample existing JSON
			fileObj.hashId = hashId
			if (fileObj.hasOwnProperty('filemeta')) {
				fileObj.filemeta.hashId = hashId
			} else {
				fileObj.filemeta = { hashId: hashId }
			}

			// Add a little sugar for the sorting in the UI
			fileObj.addedToQueue = Date.now()

			// Push the file object into the hashmap
			await commit(VUEX_ATTACHMENT_QUEUE_MANAGER_HASH_PUSH_FILE, {
				key: hashId,   // The key for the file in the hashmap
				val: fileObj   // The file object
			})

			// Now we've saved our file to the hashmap, put it in the correct starting bucket\
			let toBucket
			if (payload.forceEnqueue) {
				toBucket = AQM_QUEUED_BUCKET
			} else {
				toBucket = (fileObj.usage_type === SILA_FILE_USAGE_TYPE_JOB_ATTACHMENT ||
								fileObj.usage_type === SILA_FILE_USAGE_TYPE_ITS_ASSET ||
								fileObj.usage_type === SILA_FILE_USAGE_TYPE_ITS_PRODUCT ||
								fileObj.usage_type === SILA_FILE_USAGE_TYPE_FTP_URL) ? AQM_HOLDING_BUCKET : AQM_QUEUED_BUCKET
			}

			await commit(VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_MOVE_TO_BUCKET, {
				hashId: hashId,
				from:   null,
				to:     toBucket,
			})
		}

		// Call the upload or skip to create file if ITS Obj
		// 'autoEnqueue' forces files to upload regardless of type
        if (fileObj && fileObj.usage_type) {
            switch (fileObj.usage_type) {
                case SILA_FILE_USAGE_TYPE_ITS_ASSET :
                case SILA_FILE_USAGE_TYPE_ITS_PRODUCT :
                case SILA_FILE_USAGE_TYPE_FTP_URL :
                    // DONT START CREATE ACTION YET
                    // These file types wait until user click 'ADD ATTACHMENTS'
                    // dispatch(VUEX_FILE_CREATE_REQUEST, fileObj)
                    break

                default :
                    dispatch(VUEX_ATTACHMENT_QUEUE_MANAGER_ENQUEUE_UPLOAD)
                    break
            }
        }
	},

	// This is called form the job dialog to specify how the attachment is used
	[VUEX_ATTACHMENT_QUEUE_MANAGER_ASSIGN_USAGE_TYPE]: async({ commit, dispatch }, payload) => {

		// Set the newly chosen label
		await commit(VUEX_ATTACHMENT_QUEUE_MANAGER_ASSIGN_USAGE_TYPE, payload)

		// Now the file can be moved to queued
		/*await commit(VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_MOVE_TO_BUCKET, {
			hashId:  payload.hashId,
			from:    AQM_HOLDING_BUCKET,
			to:      AQM_QUEUED_BUCKET,
		})*/

		// Call the upload worker
		/*dispatch(VUEX_ATTACHMENT_QUEUE_MANAGER_ENQUEUE_UPLOAD)*/
	},

	[VUEX_ATTACHMENT_QUEUE_MANAGER_ASSIGN_NOTE]: async({ commit, dispatch }, payload) => {

		await commit(VUEX_ATTACHMENT_QUEUE_MANAGER_ASSIGN_NOTE, payload)
	},

	[VUEX_ATTACHMENT_QUEUE_MANAGER_REMOVE_HOLDING_FILE]: async({ commit }, payload) => {
		commit(VUEX_ATTACHMENT_QUEUE_MANAGER_REMOVE_HOLDING_FILE, payload)
	},

	[VUEX_ATTACHMENT_QUEUE_MANAGER_QUEUE_JOB_ATTACHMENTS]: async({ state, commit, dispatch }) => {
		while(state.filesHolding.length > 0) {
			// Now the file can be moved to queued
			await commit(VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_MOVE_TO_BUCKET, {
				hashId:  state.filesHolding[0],
				from:    AQM_HOLDING_BUCKET,
				to:      AQM_QUEUED_BUCKET,
			})

		}

		// Call the upload worker
		await dispatch(VUEX_ATTACHMENT_QUEUE_MANAGER_ENQUEUE_UPLOAD)
	},

	[VUEX_ATTACHMENT_QUEUE_MANAGER_CREATE_JOB_ATTACHMENTS]: async({ state, commit, dispatch }) => {

		while(state.filesHolding.length > 0) {
			// Now the file can be moved to queued
			await commit(VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_MOVE_TO_BUCKET, {
				hashId:  state.filesHolding[0],
				from:    AQM_HOLDING_BUCKET,
				to:      'filesAttaching',
			})

			await dispatch(VUEX_ATTACHMENT_QUEUE_MANAGER_ATTACH)
		}

		// Call the upload worker
	},



	/**
	 * SECTION 2 - Uploading: Includes creation of DB file, upload to s3, etc
	 */

	// 2.1
	// First, we see if there are items to upload and if the uploader is
	// free. If so, we start the process by moving a file into the uplaoding
	// bucket and then creating a file entry on the server.
	[VUEX_ATTACHMENT_QUEUE_MANAGER_ENQUEUE_UPLOAD]: async({ rootState, state, dispatch, commit }) => {
	    // Only process if there is work to do
		if (state.filesQueued.length > 0 && state.filesUploading.length < VUEX_UPLOAD_S3_QUEUE_SIZE) {

			// Get next id
			let hashId = state.filesQueued[0]

			// Move from queued to uploading
			await commit(VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_MOVE_TO_BUCKET, {
				hashId: hashId,
				from:   AQM_QUEUED_BUCKET,
				to:     AQM_UPLOADING_BUCKET,
			})

			// Create a file object first
			// We need to send a specific set of create props
			let fileObj = state.fileHash[hashId]
			let fileCreateProps = {
				name:           fileObj.name,
				filename:       fileObj.filename,
				mimetype:       fileObj.file.type,
				size:           fileObj.file.size,
				usage_type:     fileObj.usage_type,
				usage_sub_type: fileObj.usage_sub_type,
				note:           fileObj.note,
				upload_status:  SILA_FILE_UPLOAD_STATUS_PENDING,
				filemeta:       fileObj.filemeta
			}
			dispatch(VUEX_FILE_CREATE_REQUEST, fileCreateProps)

			if (!rootState.ui.globalUploader.displayState) {
				dispatch(VUEX_UI_SHOW_GLOBAL_UPLOADER)
			}

			// We may still have room, call again
			if (state.filesQueued.length > 0 && state.filesUploading.length < VUEX_UPLOAD_S3_QUEUE_SIZE) {
				dispatch(VUEX_ATTACHMENT_QUEUE_MANAGER_ENQUEUE_UPLOAD)
			}
		}
	},

	// Used for attaching SILA/FTP assets | does not upload to S3
	[VUEX_ATTACHMENT_QUEUE_MANAGER_ATTACH]: async({ rootState, state, dispatch, commit }) => {
		// Only process if there is work to do
		if (state.filesAttaching.length > 0) {

			// Get next id
			let hashId = state.filesAttaching[0]

			// Create a file object first
			// We need to send a specific set of create props
			let fileObj = state.fileHash[hashId]
			let fileCreateProps = {
				filename          : fileObj.filename,
				usage_type        : fileObj.usage_type,
				usage_sub_type    : fileObj.usage_sub_type,
				upload_status     : SILA_FILE_UPLOAD_STATUS_SUCCESS,
				processing_status : SILA_FILE_PROCESSING_STATUS_SUCCESS,
				filemeta          : fileObj.filemeta,
				uri               : fileObj.uri
			}



			dispatch(VUEX_FILE_CREATE_REQUEST, fileCreateProps)

			// We may still have room, call again
			/*if (state.filesAttaching.length > 0 ) {
				dispatch(VUEX_ATTACHMENT_QUEUE_MANAGER_ENQUEUE_UPLOAD)
			}*/
		}
	},


	// 2.2
	// When the file object has been successfully created, this gets called.
	// We persist the results of the file API call and then call the "attacher"
	// that figure sout what operations it needs to do to continue based on type
	// and things.
	//
	// Below, if we fail, we need to handle what happened, like move to the
	// failure bucket.
	[VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_CREATE_SUCCESS]: async({ commit, dispatch }, payload) => {
		// Save our new metadata to the file object
		await commit(VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_CREATE_SUCCESS, payload)

		// With file id in hand, let's do some attaching!
		dispatch(VUEX_ATTACHMENT_PERFORM_ATTACH_TO_RESOLUTION, state.fileHash[payload.filemeta.hashId])

	},

	[VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_CREATE_FAILURE]: async({ commit }, payload) => {
		commit(VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_CREATE_FAILURE, payload)

		// TODO: handle error
        // Save our new metadata to the file object
        await commit(VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_CREATE_FAILURE, payload)
	},

	// 2.3
	// Here's the action that gets called after the attachments are done. It,
	// in turn, sends our file object of for, drum roll ..... uploading! Finally!
	[VUEX_ATTACHMENT_QUEUE_MANAGER_ATTACH_TO_RESOLUTION_SUCCESS]: async({ commit, dispatch }, payload) => {
		// With our file object in place, start the upload
		switch(payload.usage_type) {
			case SILA_FILE_USAGE_TYPE_ITS_ASSET :
			case SILA_FILE_USAGE_TYPE_ITS_PRODUCT :
			case SILA_FILE_USAGE_TYPE_FTP_URL :
				await commit(VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_MOVE_TO_BUCKET, {
						hashId: payload.hashId,
						from:   'filesAttaching',
						to:     AQM_COMPLETED_BUCKET, // Skip processing bucket
						                              // TODO: BE singal not implemented yet, v2.1
					})
				break

			default :
				dispatch(VUEX_UPLOAD_REQUEST, payload)
				break
		}
	},

	// 2.4
	// Assign the S3 key to the fileObject so we can track it through S3
	[VUEX_ATTACHMENT_QUEUE_MANAGER_ASSIGN_S3_KEY]: async({ commit }, payload) => {
		commit(VUEX_ATTACHMENT_QUEUE_MANAGER_ASSIGN_S3_KEY, payload)
	},

	[VUEX_ATTACHMENT_QUEUE_MANAGER_HANDLE_UPLOAD_RESULT]: async({ state, dispatch, commit }, payload) => {
		// Save the deets to our object before continuing (status, URI, etc)
		await commit(VUEX_ATTACHMENT_QUEUE_MANAGER_HANDLE_UPLOAD_RESULT, payload)

		if (payload.status === SILA_FILE_UPLOAD_STATUS_SUCCESS) {

			// Get our object
			let fileObj = state.fileHash[payload.hashId]

			// Update the server
			await dispatch(VUEX_FILE_UPDATE_REQUEST, {
				id:                fileObj.id,
				upload_status:     fileObj.upload_status,
				processing_status: fileObj.processing_status,
				uri:               fileObj.uri
			})

			// Move on to next bucket
			await commit(VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_MOVE_TO_BUCKET, {
				hashId: payload.hashId,
				from:   AQM_UPLOADING_BUCKET,
				to:     AQM_COMPLETED_BUCKET, // Skip processing bucket
				                              // TODO: BE singal not implemented yet, v2.1
			})

			// Skip processing bucket; BE singal not implemented yet, v2.1
			// dispatch(VUEX_ATTACHMENT_QUEUE_MANAGER_PROCESS_FILE, payload)
			dispatch(VUEX_ATTACHMENT_QUEUE_MANAGER_HANDLE_PROCESSING_COMPLETE)

			dispatch(VUEX_ATTACHMENT_QUEUE_MANAGER_EVENT_DISPATCHER, fileObj)

		} else if (payload.status === SILA_FILE_UPLOAD_STATUS_FAILURE) {
            // Get our object
            let fileObj = state.fileHash[payload.hashId]

            // Update the server
            await dispatch(VUEX_FILE_UPDATE_REQUEST, {
                id:                fileObj.id,
                upload_status:     fileObj.upload_status,
                processing_status: fileObj.processing_status
            })

		    // Move on to next bucket
			await commit(VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_MOVE_TO_BUCKET, {
				hashId: payload.hashId,
				from:   AQM_UPLOADING_BUCKET,
				to:     AQM_FAILED_BUCKET,
			})

            //Show snackbar for success
            dispatch(VUEX_SNACKBAR_SHOW, {
                description_text: "An unknown file error has occurred.  This can sometimes be due to a spotty connection.  Please try again.",
                button_text: "",
                timeout: 4000,
                button_dispatch: "",
                button_dispatch_payload: {},
            })
		}

		// See if we have more uploading to do :-)
		dispatch(VUEX_ATTACHMENT_QUEUE_MANAGER_ENQUEUE_UPLOAD)
	},



	/**
	 * SECTION 3 - Processing: While the file is processing on the backend, we can update here
	 */
	// Once uploaded, the file is porcessing, BE will notify
	[VUEX_ATTACHMENT_QUEUE_MANAGER_PROCESS_FILE]: async({ state, dispatch, commit }, payload) => {
		// TODO: BE singal not implemented yet, v2.1
	},

	[VUEX_ATTACHMENT_QUEUE_MANAGER_HANDLE_PROCESSING_COMPLETE]: async({ state, getters, dispatch, commit }, payload) => {
	 	// Move on to next bucket
		// await commit(VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_MOVE_TO_BUCKET, {
		// 	hashId: payload.hashId,
		// 	from:   AQM_PROCESSING_BUCKET,
		// 	to:     AQM_COMPLETED_BUCKET,
		// })

		// UPDATE FILE STATUS HERE? PROCESSING -> COMPLETE
		let hasFiles = getters.hasQueuedFiles ||
					   getters.hasUploadingFiles ||
					   getters.hasProcessingFiles

	 	if (!hasFiles) {
			dispatch(VUEX_UI_HIDE_GLOBAL_UPLOADER, 5000)
		}
	},



	/**
	 * SECTION 4 - Completion: Successes, failures, cleanupp and teardown
	 */
	[VUEX_ATTACHMENT_QUEUE_MANAGER_EVENT_DISPATCHER]: async({ dispatch }, payload) => {
		// dispatch events in other modules based on completion/failure
		// of upload items

		/* switch(payload.attach_to.model) {
			// App/AbsenceNotice
			case SILA_FILE_ATTACHMENT_MODEL_ABSENCE_NOTICE :
			// App/TimeOffRequest
			case SILA_FILE_ATTACHMENT_MODEL_TIME_OFF_REQUEST :
	 			dispatch(VUEX_CALENDAR_FILE_UPLOAD_SUCCESS, payload)
	 			break
		} */
	},

	[VUEX_ATTACHMENT_QUEUE_MANAGER_REMOVE_FILE]: async({ dispatch, commit }, payload) => {
		return await store.dispatch('VUEX_ATTACHMENTS_DELETE_ATTACHMENT', payload).then(async (response) => {
			if (!response.status) return
			if (response.status == 200 || response.status == 204)
				await commit(VUEX_ATTACHMENT_QUEUE_MANAGER_REMOVE_FILE, payload)
				return response
		})
	}
}


const mutations = {
	//
	// Shhh, these are internal only
	// expects { key: hashId, val: fileObject }
	//
	[VUEX_ATTACHMENT_QUEUE_MANAGER_HASH_PUSH_FILE]: (state, payload) => {
		state.fileHash[payload.key] = payload.val
	},

	//
	// Shhh, these are internal only
	// expects { key: hashId }
	//
	[VUEX_ATTACHMENT_QUEUE_MANAGER_HASH_POP_FILE]: (state, payload) => {
		delete state.fileHash[payload.key]
	},


	// This little gem is what moves our hashIds from one bucket to the next
	[VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_MOVE_TO_BUCKET]: (state, payload) => {
		// Remove it from preceding bucket
		if (payload.from) {
			let index = state[payload.from].indexOf(payload.hashId)
			let removed = (index > -1) ? state[payload.from].splice(index, 1) : null
		}

		// Add it to next bucket
		// TODO: this was using PUSH to add files to array. Did this break anything??
		// used UNSHIFT for ITS assets
		state[payload.to].unshift(payload.hashId)
	},


	// When we need to assign a subtype / label for the file's usage
	[VUEX_ATTACHMENT_QUEUE_MANAGER_ASSIGN_USAGE_TYPE]: (state, payload) => {
		Object.assign(state.fileHash[payload.hashId], { usage_sub_type: payload.usage_sub_type })
	},

	[VUEX_ATTACHMENT_QUEUE_MANAGER_ASSIGN_NOTE]: (state, payload) => {
		Object.assign(state.fileHash[payload.hashId], { note: payload.note })
	},

	// Assigns comment id back to the object once we have one available
	[VUEX_ATTACHMENT_QUEUE_MANAGER_ASSIGN_COMMENT_ID]: (state, payload) => {
		Object.assign(state.fileHash[payload.hashId].attach_to, { model_id: payload.commentId })
	},

	[VUEX_ATTACHMENT_QUEUE_MANAGER_UPDATE_FILE_STATUS]: (state, payload) => {
		Object.assign(state.filesCompleted[payload.hashId], { status: payload.status })
	},

	[VUEX_ATTACHMENT_QUEUE_MANAGER_REMOVE_HOLDING_FILE]: (state, payload) => {
		let index = state.filesHolding.indexOf(payload.hashId)
		state.filesHolding.splice(index, 1)
	},

	[VUEX_ATTACHMENT_QUEUE_MANAGER_REMOVE_FILE]: (state, payload) => {

		// Get Hash
		let hash = Object.keys(state.fileHash).filter(key => state.fileHash[key].id === payload.file_id)


		//------------------
		let buckets = [
			state.filesHolding,
			state.filesQueued,
			state.filesUploading,
			state.filesProcessing,
			state.filesCompleted,
		]

		// Loop over buckets and remove hash entry
		buckets.map(bucket => {


			let index = bucket.findIndex(item => item == hash)

			if (index > -1) {
				// Delete file reference in bucket
				bucket.splice(index, 1)

				// Delete file in state.fileHash
				delete state.fileHash[hash]
				return
			}
		})

		//
		// TODO: Send delete file to Attachments
		//
	},

	// Clear defined bucket
	[VUEX_ATTACHMENT_QUEUE_MANAGER_CLEAR_BUCKET]: (state, payload) => {
		state[payload].map((file, index) => {
			delete Object.keys(state.fileHash).filter(key => key === file)
		})

		state[payload] = []
	},

	// Created a file object in the db
	[VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_CREATE_SUCCESS]: (state, payload) => {
		let hashId = payload.filemeta.hashId
		Object.assign(state.fileHash[hashId], {
			id: payload.id,
			upload_status: payload.upload_status
		})
	},

	// Failed to create a file object in the db
	[VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_CREATE_FAILURE]: (state, payload) => {
        let hashId = payload.filemeta.hashId
        Object.assign(state.fileHash[hashId], {
            id: payload.id,
            upload_status: payload.upload_status
        })
	},

	// Assign the S3 key to the file object
	[VUEX_ATTACHMENT_QUEUE_MANAGER_ASSIGN_S3_KEY]: (state, payload) => {
		state.fileHash[payload.hashId].key = payload.key
	},

	// Set the upload progress on a file object
	[VUEX_ATTACHMENT_QUEUE_MANAGER_UPLOAD_PROGRESS]: (state, payload) => {
	    Object.keys(state.fileHash).forEach((hashId) => {
			if (state.fileHash[hashId].key == payload.key) {
                let sameCount = 0 //how many ticks has this been stuck on the same number
			    if (state.fileHash[hashId].progress && state.fileHash[hashId].progress.sameCount) {
                    let sameCount = state.fileHash[hashId].progress
			        if (state.fileHash[hashId].progress == state.fileHash[hashId].total) {
                        sameCount++
                    } else {
                        sameCount = 0
                    }
                }
				state.fileHash[hashId].progress = {
					loaded: payload.loaded,
					total:  payload.total,
                    sameCount:  sameCount
				}
			}
		})
	},

	// Once the S3 upload is complete, we store the result
	// TODO: Communicate that to the server!!!!!
	[VUEX_ATTACHMENT_QUEUE_MANAGER_HANDLE_UPLOAD_RESULT]: (state, payload) => {
		state.fileHash[payload.hashId].status = payload.status
		state.fileHash[payload.hashId].upload_status = payload.status
		state.fileHash[payload.hashId].processing_status = SILA_FILE_PROCESSING_STATUS_PENDING
		state.fileHash[payload.hashId].uri = payload.uri ? payload.uri : null
	},

	// Triggered when upload starts
	[VUEX_UPLOAD_REQUEST]: (state, payload) => {
		state.fileHash[payload.hashId].status = payload.status
		state.fileHash[payload.hashId].upload_status = payload.status
	}
}

export default {
	state,
	getters,
	actions,
	mutations,
}
